import React, { useState } from "react";
import { Checkbox, FormControlLabel, IconButton } from "@mui/material";
import { Form, Table } from "react-bootstrap";
import moment from "moment";
import { FilterList as FilterListIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  ArrowUpward as FaSortUp,
  ArrowDownward as FaSortDown,
  SwapVert as FaSort,
} from "@mui/icons-material";

const CustomTable = ({
  items,
  allItems = [],
  columns,
  visibleColumns = null,
  setVisibleColumns = null,
  cantHideColumns = null,
  handleSort = null,
  sortConfig = null,
  maxHeight = "500px",
  sortingColumns = null,
  horizontalMargin = "mx-4",
  isSelectable = false,
  singleSelect = false,
  selectableIDs = [],
  selectedRows = [],
  uniqueRowKey = "",
  setSelectedRows = null,
  disabledKey = "",
  editableCells = [],
  onRowSave = null,
  isSavingRow = false,
  onRowClick = null,        // called with the row object when a row is clicked
  fixedLayout = false,      // opt-in only: enables per-column widths (used by Holdings page).
                             // Defaults to false so every other page keeps its current layout.
}) => {
  const [filterVisible, setFilterVisible] = useState(false);
  const [rows, setRows]                   = useState([]);

  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editValues, setEditValues]       = useState({});

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      const enabledRows     = allItems.filter((row) => !row[disabledKey]);
      const filteredRows    = enabledRows.map((row) => row[uniqueRowKey]);
      const selectedBodyNos = enabledRows.map((row) =>
        Object.fromEntries(selectableIDs.map((key) => [key, row[key] || ""]))
      );
      setRows(filteredRows);
      setSelectedRows(selectedBodyNos);
    } else {
      setRows([]);
      setSelectedRows([]);
    }
  };

  const toggleRow = (id) => {
    if (singleSelect) {
      const alreadySelected = rows.includes(id);
      setRows(alreadySelected ? [] : [id]);
      setSelectedRows(alreadySelected ? [] : allItems
        .filter((i) => i[uniqueRowKey] === id)
        .map((row) => Object.fromEntries(selectableIDs.map((key) => [key, row[key] || null])))
      );
      return;
    }
    setRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
    setSelectedRows((prev) =>
      prev.map((p) => p[uniqueRowKey]).includes(id)
        ? prev.filter((row) => row[uniqueRowKey] !== id)
        : [
            ...prev,
            ...allItems
              .filter((i) => i[uniqueRowKey] === id)
              .map((row) =>
                Object.fromEntries(
                  selectableIDs.map((key) => [key, row[key] || null])
                )
              ),
          ]
    );
  };

  const isAllSelected =
    selectedRows.length === allItems.filter((row) => !row[disabledKey]).length;

  const handleColumnVisibilityChange = (columnName) => {
    setVisibleColumns((prevState) => ({
      ...prevState,
      [columnName]: !prevState[columnName],
    }));
  };

  const getSortIcon = (column) => {
    if (sortConfig.key === column) {
      return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const hasRowEdit = typeof onRowSave === "function" && editableCells.length > 0;

  const handleEditStart = (item) => {
    setEditingRowKey(item[uniqueRowKey]);
    const initial = {};
    editableCells.forEach((col) => {
      const val = item[col];
      initial[col] = val && val !== "0" ? val : "";
    });
    setEditValues(initial);
  };

  const handleEditCancel = () => {
    setEditingRowKey(null);
    setEditValues({});
  };

  // PIC is the only mandatory field to save a row — RFID/NLIS ID are
  // optional (manual kill upload allows PIC-only records).
  const isSaveEnabled = () => {
    const pic = (editValues["pic"] || "").trim();
    return pic.length > 0;
  };

  const handleEditSave = (item) => {
    if (!isSaveEnabled()) return;
    onRowSave(item, editValues);
    setEditingRowKey(null);
    setEditValues({});
  };

  // Only fire onRowClick when not clicking a checkbox, button, or input
  const handleRowClick = (e, item) => {
    if (!onRowClick) return;
    if (e.target.closest("button, input, a, .MuiCheckbox-root")) return;
    onRowClick(item);
  };

  return (
    <div
      className={`table-responsive mt-3 ${horizontalMargin}`}
      style={{
        maxHeight: maxHeight,
        overflow: "auto",
        backgroundColor: "#eee",
        padding: "16px",
        borderRadius: "8px",
      }}>
      {items.length > 0 ? (
        <div style={{ position: "relative" }}>
          <IconButton
            onClick={() => setFilterVisible((p) => !p)}
            style={{ position: "absolute", top: -40, right: 10 }}
            title="Filter columns">
            <FilterListIcon />
          </IconButton>

          {filterVisible && (
            <div
              style={{
                position:        "absolute",
                top:             0,
                right:           10,
                backgroundColor: "white",
                padding:         "5px",
                border:          "1px solid #ddd",
                fontSize:        "10px",
              }}>
              {columns.map(
                (item) =>
                  (!cantHideColumns || !cantHideColumns.includes(item.value)) && (
                    <FormControlLabel
                      key={item.name}
                      control={
                        <Checkbox
                          checked={visibleColumns[item.name]}
                          onChange={() => handleColumnVisibilityChange(item.name)}
                        />
                      }
                      label={item.name}
                    />
                  )
              )}
            </div>
          )}

          <Table
            striped
            bordered
            hover
            className="mt-5"
            style={fixedLayout ? { tableLayout: "fixed", width: "100%" } : undefined}>
            {fixedLayout && (
              <colgroup>
                {isSelectable && <col style={{ width: "40px" }} />}
                <col style={{ width: "4%" }} />
                {columns.map(
                  (item, index) =>
                    visibleColumns[item.name] && (
                      <col key={index} style={{ width: item.width || "auto" }} />
                    )
                )}
                {hasRowEdit && <col style={{ width: "100px" }} />}
              </colgroup>
            )}
            <thead>
              <tr>
                {isSelectable && (
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th style={{ width: fixedLayout ? "4%" : undefined }}>#</th>
                {columns.map(
                  (item, index) =>
                    visibleColumns[item.name] && (
                      <th
                        key={index}
                        onClick={() => handleSort && handleSort(item.value)}
                        style={{
                          cursor: "pointer",
                          width: fixedLayout ? (item.width || "auto") : undefined,
                        }}>
                        {item.name}{" "}
                        {sortingColumns &&
                          sortingColumns.includes(item.value) &&
                          getSortIcon(item.value)}
                      </th>
                    )
                )}
                {hasRowEdit && <th style={{ width: 100 }}>Edit</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const isEditingThisRow = editingRowKey === item[uniqueRowKey];
                const isUploaded       = !!item[disabledKey];

                return (
                  <tr
                    key={item[uniqueRowKey] ?? index}
                    onClick={(e) => handleRowClick(e, item)}
                    style={{
                      cursor: onRowClick ? "pointer" : undefined,
                      backgroundColor: isEditingThisRow
                        ? "#fffbe6"
                        : isUploaded
                        ? "#e6ffe6"
                        : undefined,
                    }}>
                    {isSelectable && (
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={rows.includes(item[uniqueRowKey])}
                          onChange={() => toggleRow(item[uniqueRowKey])}
                          disabled={isUploaded}
                        />
                      </td>
                    )}
                    <td style={{ width: fixedLayout ? "4%" : undefined }}>{index + 1}</td>

                    {columns.map(
                      (itemKey) =>
                        visibleColumns[itemKey.name] &&
                        (itemKey.value === "production_date" ? (
                          <td key={itemKey.value}>
                            {moment(item[itemKey.value]).format("YYYY-MM-DD")}
                          </td>
                        ) : isEditingThisRow && editableCells.includes(itemKey.value) ? (
                          <td key={itemKey.value}>
                            <input
                              className="form-control form-control-sm"
                              style={{ minWidth: 80, maxWidth: 120, fontSize: 11 }}
                              value={editValues[itemKey.value] ?? ""}
                              onChange={(e) =>
                                setEditValues((prev) => ({
                                  ...prev,
                                  [itemKey.value]: e.target.value,
                                }))
                              }
                              disabled={isSavingRow}
                              placeholder={
                                itemKey.value === "pic"
                                  ? "PIC *"
                                  : itemKey.value === "rfid_no"
                                  ? "RFID"
                                  : itemKey.value === "nlis_id"
                                  ? "NLIS ID"
                                  : itemKey.name
                              }
                            />
                          </td>
                        ) : (
                          <td
                            key={itemKey.value}
                            title={item[itemKey.value] ?? ""}
                            style={
                              fixedLayout
                                ? {
                                    width: itemKey.width || "auto",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }
                                : undefined
                            }>
                            {item[itemKey.value] ?? ""}
                          </td>
                        ))
                    )}

                    {hasRowEdit && (
                      <td>
                        {isUploaded ? (
                          <span style={{ fontSize: 11, color: "#888" }}>Uploaded</span>
                        ) : isEditingThisRow ? (
                          <div className="d-flex flex-column gap-1">
                            <button
                              className="btn btn-sm btn-dark"
                              style={{ fontSize: 11, padding: "2px 6px" }}
                              onClick={() => handleEditSave(item)}
                              disabled={isSavingRow || !isSaveEnabled()}
                              title={
                                !isSaveEnabled()
                                  ? "PIC is required"
                                  : "Save"
                              }>
                              {isSavingRow ? "Saving…" : "Save"}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={handleEditCancel}
                              disabled={isSavingRow}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{ fontSize: 11, padding: "2px 6px" }}
                            onClick={() => handleEditStart(item)}
                            disabled={!!editingRowKey || isSavingRow}
                            title="Edit RFID / NLIS ID / PIC">
                            <EditIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center fs-5 py-2">No Items found</div>
      )}
    </div>
  );
};

export default CustomTable;
